import React, { useState, useEffect } from "react";
import {
  Button,
  Paper,
  Typography,
  Box,
  Divider,
  Stepper,
  Step,
  StepButton,
  MobileStepper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import { useParams, useNavigate } from "react-router-dom";
import {
  UpdateClient,
  GetClientByAccount,
  SaveNewClient,
} from "../../../services/ClientsMaintenanceService";
import { GetVlpReports } from "../../../services/ReportSettingsService";
import CommonLoader from "../../common/CommonLoader";

import { STEPS, INITIAL_FORM_STATE } from "./clientFormHelpers";
import BasicInfoStep from "./BasicInfoStep";
import ReportingStep from "./ReportingStep";
import BillingStep from "./BillingStep";
import ShippingStep from "./ShippingStep";
import FieldStep from "./FieldStep";
import ManagementStep from "./ManagementStep";
import SystemStep from "./SystemStep";
import NotesSettingsStep from "./NotesSettingsStep";
import SampleLocationsDialog from "./SampleLocationsDialog";
import BottleOrderSchedulesDialog from "./BottleOrderSchedulesDialog";
import MiscChargesDialog from "./MiscChargesDialog";
import OperatorAssignmentDialog from "./OperatorAssignmentDialog";
import SimpleNameListDialog from "./SimpleNameListDialog";
import OrderTemplatesDialog from "./OrderTemplatesDialog";
import {
  GetFacilityIds, SaveFacilityId, DeleteFacilityId,
  GetSamplingPointIds, SaveSamplingPointId, DeleteSamplingPointId,
} from "../../../services/ClientsMaintenanceService";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DATE_FIELDS = ["expirationDate", "dateCreated", "timestamp", "contractExp"];

const buildPayload = (formData) => ({
  ...formData,
  // Booleans
  isDeleted: formData.isDeleted === true,
  taxable: formData.taxable === true,
  shortTerm: formData.shortTerm === true,
  hold: formData.hold === true,
  reportToLocalAgency: formData.reportToLocalAgency === true,
  reportToFederalAgency: formData.reportToFederalAgency === true,
  carbonCopy: formData.carbonCopy === true,
  faxReport: formData.faxReport === true,
  grantaccesstoallusers: formData.grantaccesstoallusers === true,
  surchargeExempt: formData.surchargeExempt === true,
  printBit: formData.printBit === true,
  emailBit: formData.emailBit === true,
  eomBit: formData.eomBit === true,
  eowBit: formData.eowBit === true,
  sendToRegulatory: formData.sendToRegulatory === true,
  emailRegulatoryOnly: formData.emailRegulatoryOnly === true,
  enableSampleReceipt: formData.enableSampleReceipt === true,
  shouldReceiveCoC: formData.shouldReceiveCoC === true,
  newAccountExempt: formData.newAccountExempt === true,
  syncWithKeap: formData.syncWithKeap === true,
  // Numbers
  discountPercent: formData.discountPercent ? parseFloat(formData.discountPercent) : null,
  surchargePercent: formData.surchargePercent ? parseFloat(formData.surchargePercent) : null,
  taDays: formData.taDays ? parseInt(formData.taDays, 10) : null,
  orderTemplate: formData.orderTemplate ? parseInt(formData.orderTemplate, 10) : null,
  creditAmount: formData.creditAmount ? parseFloat(formData.creditAmount) : 0,
  recordid: formData.recordid ? parseInt(formData.recordid, 10) : null,
  reportingPhone1Type: formData.reportingPhone1Type ? parseInt(formData.reportingPhone1Type, 10) : null,
  reportingPhone2Type: formData.reportingPhone2Type ? parseInt(formData.reportingPhone2Type, 10) : null,
  shippingPhone1Type: formData.shippingPhone1Type ? parseInt(formData.shippingPhone1Type, 10) : null,
  shippingPhone2Type: formData.shippingPhone2Type ? parseInt(formData.shippingPhone2Type, 10) : null,
  billingPhone1Type: formData.billingPhone1Type ? parseInt(formData.billingPhone1Type, 10) : null,
  billingPhone2Type: formData.billingPhone2Type ? parseInt(formData.billingPhone2Type, 10) : null,
  // Dates
  expirationDate: formData.expirationDate ? new Date(formData.expirationDate).toISOString() : null,
  dateCreated: formData.dateCreated ? new Date(formData.dateCreated).toISOString() : null,
  timestamp: formData.timestamp ? new Date(formData.timestamp).toISOString() : null,
  contractExp: formData.contractExp ? new Date(formData.contractExp).toISOString() : null,
  // Defaults
  labReportFileFormat: formData.labReportFileFormat || "PDF",
  invoiceReportFileFormat: formData.invoiceReportFileFormat || "PDF",
});

// ─── Component ────────────────────────────────────────────────────────────────

const EditClient = () => {
  const { accountNumber } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [loading, setLoading] = useState(false);
  const [reportsData, setReportsData] = useState([]);
  const [sampleLocationsOpen, setSampleLocationsOpen] = useState(false);
  const [bottleSchedulesOpen, setBottleSchedulesOpen] = useState(false);
  const [miscChargesOpen, setMiscChargesOpen] = useState(false);
  const [operatorsOpen, setOperatorsOpen] = useState(false);
  const [facilityIdsOpen, setFacilityIdsOpen] = useState(false);
  const [samplingPointIdsOpen, setSamplingPointIdsOpen] = useState(false);
  const [orderTemplatesOpen, setOrderTemplatesOpen] = useState(false);

  useEffect(() => {
    fetchReports();
    if (accountNumber !== undefined) loadClient();
  }, [accountNumber]);

  const loadClient = async () => {
    try {
      if (accountNumber === "add-new") return;
      setLoading(true);
      const res = await GetClientByAccount(accountNumber);
      const r = res.results;
      setFormData({
        ...INITIAL_FORM_STATE,
        ...Object.fromEntries(
          Object.keys(INITIAL_FORM_STATE).map((key) => {
            const val = r[key];
            if (typeof INITIAL_FORM_STATE[key] === "boolean") return [key, val || false];
            if (DATE_FIELDS.includes(key)) return [key, val ? val.slice(0, 10) : ""];
            return [key, val ?? ""];
          })
        ),
        labReportFileFormat: r.labReportFileFormat || "PDF",
        invoiceReportFileFormat: r.invoiceReportFileFormat || "PDF",
      });
    } catch {
      NotificationManager.error("Failed to load client data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await GetVlpReports("All");
      setReportsData(response.results || []);
    } catch (error) {
      console.error("Error fetching reports:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const validateForm = () => {
    const required = [
      ["clientName", "Client Name"],
      ["reportingName", "Reporting Name"],
      ["reportAddress", "Report Address"],
      ["reportCity", "Report City"],
      ["reportState", "Report State"],
      ["reportZip", "Report Zip Code"],
    ];
    for (const [field, label] of required) {
      if (!formData[field]) {
        NotificationManager.error(`${label} is required.`);
        return false;
      }
    }
    return true;
  };

  const handleSaveClick = async () => {
    if (!validateForm()) return;
    const data = buildPayload(formData);

    if (accountNumber !== undefined) {
      try {
        setLoading(true);
        await UpdateClient(data);
        NotificationManager.success("Client updated successfully.");
        window.location.reload();
      } catch {
        NotificationManager.error("Failed to update client.");
      } finally {
        setLoading(false);
      }
    } else {
      try {
        setLoading(true);
        await SaveNewClient(data);
        NotificationManager.success("Client saved successfully.");
        navigate("/vlp/clients");
      } catch {
        NotificationManager.error("Failed to save client.");
      } finally {
        setLoading(false);
      }
    }
  };

  const stepProps = { formData, handleChange };

  const stepContent = [
    <BasicInfoStep {...stepProps} accountNumber={accountNumber} />,
    <ReportingStep {...stepProps} />,
    <BillingStep {...stepProps} />,
    <ShippingStep {...stepProps} />,
    <FieldStep {...stepProps} />,
    <ManagementStep {...stepProps} />,
    <SystemStep {...stepProps} reportsData={reportsData} />,
    <NotesSettingsStep {...stepProps} accountNumber={accountNumber} />,
  ];

  return (
    <>
      {loading && <CommonLoader loading={loading} />}
      <div className="vlp-component-container">
        <span className="title">Client Management</span>
        <hr />

        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
          {/* ── Stepper ── */}
          {isMobile ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 1 }}>
                Step {activeStep + 1} of {STEPS.length}: <strong>{STEPS[activeStep]}</strong>
              </Typography>
              <MobileStepper
                variant="progress"
                steps={STEPS.length}
                activeStep={activeStep}
                sx={{ background: "transparent", p: 0 }}
                nextButton={null}
                backButton={null}
              />
            </Box>
          ) : (
            <Stepper
              nonLinear
              activeStep={activeStep}
              alternativeLabel
              sx={{ mb: 4, overflowX: "auto", pb: 1 }}
              style={{height: "100px"}}
            >
              {STEPS.map((label, index) => (
                <Step key={label}>
                  <StepButton onClick={() => setActiveStep(index)}>
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          )}

          {/* ── Section Title ── */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" color="primary" fontWeight={600}>
              {STEPS[activeStep]}
            </Typography>
            <Divider sx={{ mt: 1 }} />
          </Box>

          {/* ── Step Content ── */}
          <Box sx={{ minHeight: 300 }}>
            {stepContent[activeStep]}
          </Box>

          {/* ── Navigation ── */}
          <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                disabled={activeStep === 0 || loading}
                onClick={() => setActiveStep((s) => s - 1)}
              >
                Back
              </Button>
              {activeStep < STEPS.length - 1 && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setActiveStep((s) => s + 1)}
                  disabled={loading}
                >
                  Next
                </Button>
              )}
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {accountNumber && accountNumber !== "add-new" && (
                <>
                  <Button variant="outlined" color="info" size="small" onClick={() => setMiscChargesOpen(true)} disabled={loading}>
                    Misc Charges
                  </Button>
                  <Button variant="outlined" color="info" size="small" onClick={() => setOperatorsOpen(true)} disabled={loading}>
                    Operators
                  </Button>
                  <Button variant="outlined" color="info" size="small" onClick={() => setFacilityIdsOpen(true)} disabled={loading}>
                    Facility IDs
                  </Button>
                  <Button variant="outlined" color="info" size="small" onClick={() => setSamplingPointIdsOpen(true)} disabled={loading}>
                    Sampling Point IDs
                  </Button>
                  <Button variant="outlined" color="info" size="small" onClick={() => setOrderTemplatesOpen(true)} disabled={loading}>
                    Order Templates
                  </Button>
                  <Button variant="outlined" color="info" size="small" onClick={() => setSampleLocationsOpen(true)} disabled={loading}>
                    Sample Locations
                  </Button>
                  <Button variant="outlined" color="info" size="small" onClick={() => setBottleSchedulesOpen(true)} disabled={loading}>
                    Bottle Order Schedules
                  </Button>
                </>
              )}
              <Button
                variant="outlined"
                onClick={() => navigate("/vlp/maintenance-client")}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveClick}
                disabled={loading}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Paper>

        {accountNumber && (formData.lastUpdatedBy || formData.timestamp) && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block", textAlign: "right" }}>
            Last Updated:{" "}
            {formData.timestamp ? new Date(formData.timestamp).toLocaleDateString("en-US") : "—"}
            {" "}By: {formData.lastUpdatedBy || "—"}
          </Typography>
        )}

        <MiscChargesDialog
          open={miscChargesOpen}
          onClose={() => setMiscChargesOpen(false)}
          accountNumber={accountNumber}
        />
        <OperatorAssignmentDialog
          open={operatorsOpen}
          onClose={() => setOperatorsOpen(false)}
          accountNumber={accountNumber}
        />
        <SimpleNameListDialog
          open={facilityIdsOpen}
          onClose={() => setFacilityIdsOpen(false)}
          title="Facility IDs"
          accountNumber={accountNumber}
          loadFn={GetFacilityIds}
          saveFn={SaveFacilityId}
          deleteFn={DeleteFacilityId}
        />
        <SimpleNameListDialog
          open={samplingPointIdsOpen}
          onClose={() => setSamplingPointIdsOpen(false)}
          title="Sampling Point IDs"
          accountNumber={accountNumber}
          loadFn={GetSamplingPointIds}
          saveFn={SaveSamplingPointId}
          deleteFn={DeleteSamplingPointId}
        />
        <OrderTemplatesDialog
          open={orderTemplatesOpen}
          onClose={() => setOrderTemplatesOpen(false)}
          accountNumber={accountNumber}
        />
        <SampleLocationsDialog
          open={sampleLocationsOpen}
          onClose={() => setSampleLocationsOpen(false)}
          accountNumber={accountNumber}
        />
        <BottleOrderSchedulesDialog
          open={bottleSchedulesOpen}
          onClose={() => setBottleSchedulesOpen(false)}
          accountNumber={accountNumber}
        />
      </div>
    </>
  );
};

export default EditClient;
